<template>
    <b-modal id="createModal" body-class="px-0" ref="modal" title="Create a fertiliser" v-model="show" @ok="handleSubmit" @hide="close"
             @shown="focusNameInput">

        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Base Details</h6>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <label>Fertiliser Name</label>
                    <div class="position-relative">
                        <input type="text" ref="nameInput" name="name" v-validate="{required: true}" class="form-control" v-model="name">
                        <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-sm-6">
                        <div class="form-group">
                            <label>Fertiliser State</label>
                            <div>
                                <select v-model="is_liquid" class="form-control">
                                    <option :value="true">Liquid</option>
                                    <option :value="false">Solid</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-sm-6">
                        <div class="form-group">
                            <label>Fertiliser Type</label>
                            <div>
                                <select v-model="type" class="form-control">
                                    <option value="base">Base</option>
                                    <option value="folar">Folar</option>
                                    <option value="fertigation">Fertigation</option>
                                    <option value="soil_conditioner">Soil Conditioner</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Macro-Micro Elements</h6>
            </div>
            <div class="card-body">

                <div class="row">

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Boron (B)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="boron" class="form-control"
                                     :class="{'is-invalid': errors.has('boron')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="boron"
                                     data-vv-as="Boron"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('boron')">{{ errors.first('boron') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Calcium (Ca)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="calcium" class="form-control"
                                     :class="{'is-invalid': errors.has('calcium')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="calcium"
                                     data-vv-as="Calcium"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('calcium')">{{ errors.first('calcium') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Chlorine (Cl)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="chlorine" class="form-control"
                                     :class="{'is-invalid': errors.has('chlorine')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="chlorine"
                                     data-vv-as="Chlorine"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('chlorine')">{{ errors.first('chlorine') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Copper (Cu)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="copper" class="form-control"
                                     :class="{'is-invalid': errors.has('copper')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="copper"
                                     data-vv-as="Copper"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('copper')">{{ errors.first('copper') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Iron (Fe)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="iron" class="form-control"
                                     :class="{'is-invalid': errors.has('iron')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="iron"
                                     data-vv-as="Iron"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('iron')">{{ errors.first('iron') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Potassium (K)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="potassium" class="form-control"
                                     :class="{'is-invalid': errors.has('potassium')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="potassium"
                                     data-vv-as="Potassium"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('potassium')">{{ errors.first('potassium') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Magnesium (Mg)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="magnesium" class="form-control"
                                     :class="{'is-invalid': errors.has('magnesium')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="magnesium"
                                     data-vv-as="Magnesium"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('magnesium')">{{ errors.first('magnesium') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Manganese (Mn)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="manganese" class="form-control"
                                     :class="{'is-invalid': errors.has('manganese')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="manganese"
                                     data-vv-as="Manganese"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('manganese')">{{ errors.first('manganese') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Molybdenum (Mo)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="molybdenum" class="form-control"
                                     :class="{'is-invalid': errors.has('molybdenum')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="molybdenum"
                                     data-vv-as="Molybdenum"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('molybdenum')">{{ errors.first('molybdenum') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Nitrogen (N)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="nitrogen" class="form-control"
                                     :class="{'is-invalid': errors.has('nitrogen')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="nitrogen"
                                     data-vv-as="Nitrogen"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('nitrogen')">{{ errors.first('nitrogen') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Nickel (Ni)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="nickel" class="form-control"
                                     :class="{'is-invalid': errors.has('nickel')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="nickel"
                                     data-vv-as="Nickel"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('nickel')">{{ errors.first('nickel') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Phosphorus (P)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="phosphorus" class="form-control"
                                     :class="{'is-invalid': errors.has('phosphorus')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="phosphorus"
                                     data-vv-as="Phosphorus"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('phosphorus')">{{ errors.first('phosphorus') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Sulphur (S)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="sulphur" class="form-control"
                                     :class="{'is-invalid': errors.has('sulphur')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="sulphur"
                                     data-vv-as="Sulphur"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('sulphur')">{{ errors.first('sulphur') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Selenium (Se)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="selenium" class="form-control"
                                     :class="{'is-invalid': errors.has('selenium')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="selenium"
                                     data-vv-as="Selenium"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('selenium')">{{ errors.first('selenium') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-md-4 col-lg-3">
                        <label>Zinc (Zn)</label>
                        <div class="position-relative">
                            <VueMask v-model.number="zinc" class="form-control"
                                     :class="{'is-invalid': errors.has('zinc')}"
                                     placeholder="0..."
                                     v-validate="validation.percentage"
                                     data-vv-name="zinc"
                                     data-vv-as="Zinc"
                                     :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('zinc')">{{ errors.first('zinc') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Blend Additives</h6>
            </div>
            <div class="card-body">
                <b-table hover small class="no-borders" stacked="md" :items="entryRows" :fields="entriesTable.fields" :show-empty="true">
                    <template slot="additive" slot-scope="row">
                        <div class="position-relative">
                            <VueSelect
                                    data-vv-as="additive"
                                    placeholder="Additive"
                                    label="name"
                                    track-by="id"
                                    v-b-tooltip="errors.first(`additive_${row.index}`) || ''"
                                    v-validate="validation.entry_additive"
                                    :value="row.item.additive"
                                    :data-vv-name="`additive_${row.index}`"
                                    :options="additives"
                                    :allow-empty="false"
                                    :show-labels="false"
                                    @input="setRow('additive', row.index, $event)">
                                <div slot="noResult" class="text-center">
                                    <small class="text-muted">No matches</small>
                                </div>
                            </VueSelect>
                            <i class="form-group__bar"></i>
                        </div>
                    </template>
                    <template slot="percentage" slot-scope="row">
                        <div class="position-relative">
                            <VueMask
                                    class="form-control"
                                    placeholder="0..."
                                    v-validate="validation.percentage"
                                    v-b-tooltip="errors.first(`percentage_${row.index}`) || ''"
                                    data-vv-as="percentage"
                                    :value="row.item.percentage"
                                    :class="{'is-invalid': errors.has(`percentage_${row.index}`)}"
                                    :data-vv-name="`percentage_${row.index}`"
                                    :options="{numeral: true, min:0, max:100, numeralThousandsGroupStyle: 'thousand'}"
                                    @input="setRow('percentage', row.index, $event)" required />
                            <i class="form-group__bar"></i>
                        </div>
                    </template>
                    <template slot="remove" slot-scope="row">
                        <b-button size="sm" variant="outline-danger" @click="removeRow(row.index)">Remove</b-button>
                    </template>
                </b-table>
                <div class="mx-3">
                    <button type="button" class="btn btn-block btn-outline-success" @click.prevent="addRow">
                        <i class="zmdi zmdi-plus"></i> Add another row
                    </button>
                </div>
            </div>
        </div>

        <template slot="modal-footer">
            <button class="btn btn-secondary" @click="close">
                Cancel
            </button>
            <button class="btn btn-primary" :disabled="okDisabled" @click="handleSubmit">
                Save
            </button>
        </template>
    </b-modal>
</template>


<script>
import listFertiliserQuery from '@views/Fertilisers/Browse/queryListFertilisers.graphql';
import createFertiliserMutation from '@views/Fertilisers/CreateModal/mutationCreateFertiliser.graphql';
import listFertiliserAdditivesQuery from '@views/Fertilisers/CreateModal/queryListFertiliserAdditives.graphql';

export default {
  name: 'create-modal',
  apollo: {
    additives: {
      query: listFertiliserAdditivesQuery,
      update: data => data.filterFertiliserAdditives.data,
    },
  },
  props: {
    close: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      name: '',
      is_liquid: false,
      type: 'base',
      boron: 0,
      calcium: 0,
      chlorine: 0,
      copper: 0,
      iron: 0,
      potassium: 0,
      magnesium: 0,
      manganese: 0,
      molybdenum: 0,
      nitrogen: 0,
      nickel: 0,
      phosphorus: 0,
      sulphur: 0,
      selenium: 0,
      zinc: 0,
      additives: [],
      entryRows: [{ additive: null, percentage: null }],
      show: true,
      loading: 0,
      validation: {
        is_liquid: { required: true },
        entry_additive: { required: true },
        percentage: { required: true, min_value: 0 },
      },
      entriesTable: {
        fields: [
          { key: 'additive', label: 'Additive' },
          { key: 'percentage', label: 'Percentage' },
          { key: 'remove', label: 'Remove', 'class': 'text-right' },
        ],
      },
    };
  },
  computed: {
    okDisabled() {
      return this.name.length === 0;
    },
  },
  methods: {
    addRow() {
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful) {
          this.entryRows.push({
            additive: null,
            percentage: null,
          });
        }
      });
    },
    removeRow(index) {
      this.entryRows.splice(index, 1);
    },
    setRow(field, index, value) {
      this.entryRows[index][field] = value;
    },
    handleSubmit() {
      if (this.okDisabled) {
        return false;
      }
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          return;
        }

        const input = {
          name: this.name,
          is_liquid: this.is_liquid,
          type: this.type,
          boron: this.boron,
          calcium: this.calcium,
          chlorine: this.chlorine,
          copper: this.copper,
          iron: this.iron,
          potassium: this.potassium,
          magnesium: this.magnesium,
          manganese: this.manganese,
          molybdenum: this.molybdenum,
          nitrogen: this.nitrogen,
          nickel: this.nickel,
          phosphorus: this.phosphorus,
          sulphur: this.sulphur,
          selenium: this.selenium,
          zinc: this.zinc,
          additives: this.entryRows.map(row => ({
            id: row.additive.id,
            percentage: row.percentage,
          })),
        };

        this.$apollo
          .mutate({
            mutation: createFertiliserMutation,
            refetchQueries: [
              { query: listFertiliserQuery, variables: { input: { include_deleted: true } } },
              { query: listFertiliserQuery },
            ],
            variables: {
              input,
            },
          })
          .then(() => {
            this.$notifySuccess('Fertiliser created!');
            this.close();
          })
          .catch(err => this.$mapGraphQLError(err))
          .catch(err => {
            console.error(err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      });
    },
    focusNameInput() {
      this.$refs.nameInput.focus();
    },
  },
};
</script>
